/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/class/KavenUInt64.ts
 * @create:      2019-04-11 13:01:36.287
 * @modify:      2026-03-12 14:03:36.178
 * @version:     6.1.1
 * @times:       77
 * @lines:       201
 * @copyright:   Copyright © 2019-2026 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { ArraySplit } from "../array/ArraySplit";
import { Swap32 } from "../number/Swap32";
import { ToHex } from "../number/ToHex";
import { SplitByN } from "../string/SplitByN";

/**
 *
 * @since 4.0.0
 * @version 2022-06-26
 */
export class KavenUInt64 {
    public get HEX() {
        // this.ToUnit();

        return ToHex(this.high, 8) + ToHex(this.low, 8);
    }

    public static FromBytes(...bytes: number[]) {
        let [h, l] = [0, 0];

        const hl = ArraySplit(bytes, 4);

        if (hl.length === 2) {
            for (let i = 0; i < hl[0].length; i++) {
                l += (hl[0][i] << 8 * i);
            }

            for (let i = 0; i < hl[1].length; i++) {
                h += (hl[1][i] << 8 * i);
            }
        } else {
            for (let i = 0; i < hl[0].length; i++) {
                l += (hl[0][i] << 8 * i);
            }
        }

        return KavenUInt64.FromHighLow(h, l);
    }

    public static FromHighLow(high: number, low: number) {
        const result = new KavenUInt64();

        result.high = high >>> 0;
        result.low = low >>> 0;

        return result;
    }

    public static Initialize2d(x: number, y: number) {
        return new Array(x).fill(new KavenUInt64()).map(() => new Array(y).fill(new KavenUInt64()));
    }

    protected high = 0;
    protected low = 0;

    public constructor();
    public constructor(value: number);
    public constructor(value: string);
    public constructor(value: string, radix: number);
    public constructor(value?: number | string, radix = 16) {
        if (value === undefined) {
            return;
        }

        if (typeof value === "number") {
            this.low = value;
        } else {
            const hl = SplitByN(value, 8);

            if (hl.length === 2) {
                this.high = parseInt(hl[0], radix);
                this.low = parseInt(hl[1], radix);
            } else {
                this.low = parseInt(hl[0], radix);
            }
        }
    }

    public Swap() {
        return KavenUInt64.FromHighLow(Swap32(this.low), Swap32(this.high));
    }

    public AND(val: KavenUInt64 | number) {
        let h: number, l: number;

        if (typeof val === "number") {
            l = this.low & val;
            h = 0;
        } else {
            l = this.low & val.low;
            h = this.high & val.high;
        }

        return KavenUInt64.FromHighLow(h, l);
    }

    public XOR(val: KavenUInt64 | number) {
        let h: number, l: number;

        if (typeof val === "number") {
            l = this.low ^ val;
            h = 0;
        } else {
            l = this.low ^ val.low;
            h = this.high ^ val.high;
        }

        return KavenUInt64.FromHighLow(h, l);
    }

    public NOT() {
        return KavenUInt64.FromHighLow(~this.high, ~this.low);
    }

    public LeftRotate(n: number) {
        if (n === 0) {
            return this.Clone();
        }

        let [h, l] = [this.low, this.high];

        if (n < 32) {
            const m = 32 - n;
            l = this.low << n | this.high >>> m;
            h = this.high << n | this.low >>> m;
        } else if (n > 32) {
            n -= 32;
            const m = 32 - n;
            l = this.high << n | this.low >>> m;
            h = this.low << n | this.high >>> m;
        }

        return KavenUInt64.FromHighLow(h, l);
    }

    public RightRotate(n: number) {
        if (n === 0) {
            return this.Clone();
        }

        let [h, l] = [this.low, this.low];

        if (n < 32) {
            const m = 32 - n;
            l = this.low >>> n | this.high << m;
            h = this.high >>> n | this.low << m;
        } else if (n > 32) {
            n -= 32;
            const m = 32 - n;
            l = this.high >>> n | this.low << m;
            h = this.low >>> n | this.high << m;
        }

        return KavenUInt64.FromHighLow(h, l);
    }

    public Clone() {
        return KavenUInt64.FromHighLow(this.high, this.low);
    }

    public Equals(another: KavenUInt64 | number): boolean {
        if (typeof another === "number") {
            return this.Equals(new KavenUInt64(another));
        }

        return this.high === another.high && this.low === another.low;
    }
}
